/**
 * LA MESURE D'AUDIENCE MONTÉE AU-DESSUS DES ROUTES.
 *
 * Deux abonnements, et rien d'autre :
 *   · le routeur — chaque changement de `location` pousse une page vue
 *     virtuelle. C'est la seule façon de mesurer un `BrowserRouter` : le
 *     document n'est chargé qu'une fois, donc GTM ne voit qu'une page.
 *   · le consentement — sur le patron de `ContentsquareProvider` et
 *     `IntercomProvider`, il est ÉCOUTÉ et non lu une fois : accepter depuis le
 *     bandeau doit suffire, sans rechargement.
 *
 * POURQUOI PAS DE PAGE VUE AU PREMIER RENDU ? Il y en a une, et elle est
 * délibérée. Le snippet GTM de `index.html` pousse déjà `gtm.js`, sur lequel un
 * déclencheur « toutes les pages » se branche — mais ce déclencheur ne connaît
 * ni le titre posé par `<Seo>` (il court avant React) ni la langue résolue par
 * `src/i18n`. Émettre `virtual_pageview` dès le premier rendu donne au
 * conteneur UN seul événement à écouter pour toutes les pages vues, premier
 * chargement compris, avec le même jeu de champs. Le déclencheur GA4 doit donc
 * être `virtual_pageview` SEUL, et la mesure améliorée « pages vues sur
 * changement d'historique » restée désactivée : les deux ensemble compteraient
 * chaque navigation deux fois, exactement le double comptage que les flux
 * « Aikka » et « PharmaGEO » ont produit tout l'été.
 *
 * À placer au-dessus des routes et SOUS le routeur — il lit `useLocation`.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { pushConsentUpdate, pushVirtualPageview } from '../../lib/analytics';
import { readConsent, subscribeConsent } from '../../lib/cookieConsent';

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  // Le consentement d'abord : l'état connu est poussé au montage, puis chaque
  // changement. Sans ce premier envoi, un visiteur qui a accepté lors d'une
  // visite précédente arriverait en « denied » jusqu'à ce qu'il rouvre le
  // bandeau — c'est-à-dire jamais.
  useEffect(() => {
    pushConsentUpdate(readConsent());
    return subscribeConsent(pushConsentUpdate);
  }, []);

  useEffect(() => {
    const path = `${location.pathname}${location.search}${location.hash}`;
    // Une même clé deux fois de suite n'est pas une page vue : React peut
    // réexécuter l'effet (StrictMode en développement, remontage d'un parent)
    // sans que le visiteur ait navigué.
    if (lastPath.current === path) return;
    lastPath.current = path;

    // Un cran après la peinture : `<Seo>` pose le titre dans son propre effet,
    // et une page vue qui porte le titre de la page PRÉCÉDENTE est plus
    // trompeuse qu'une page vue absente. Le `requestAnimationFrame` suffit —
    // les deux effets sont dans le même cycle de rendu.
    const frame = requestAnimationFrame(() => pushVirtualPageview(location));
    return () => cancelAnimationFrame(frame);
  }, [location]);

  return <>{children}</>;
}
